import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { MapPin, Landmark, CalendarDays, Users, ChevronLeft, Footprints, Plus, Share2 } from "lucide-react";
import api from "@/lib/api";
import AIContentBlock from "@/components/AIContentBlock";
import PronounceButton from "@/components/PronounceButton";
import TrailMap from "@/components/TrailMap";
import useTranslated from "@/hooks/useTranslated";
import { EventCard } from "@/components/home/EventsSection";

export default function CityDetail() {
  const { slug, citySlug } = useParams();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const blurbT = useTranslated(`city-blurb-${slug}-${citySlug}`, data ? [data.city.blurb] : []);

  useEffect(() => {
    setData(null);
    setNotFound(false);
    api.get(`/countries/${slug}/cities/${citySlug}`).then((r) => setData(r.data)).catch(() => setNotFound(true));
  }, [slug, citySlug]);

  if (notFound) return <div className="py-24 text-center text-slate-500" data-testid="city-not-found">City not found. <Link to={`/places/${slug}`} className="text-pink-600 font-bold">Back to country</Link></div>;
  if (!data) return <div className="min-h-[50vh] flex items-center justify-center"><span className="eq-bars" style={{ height: 36 }}><span /><span /><span /><span /><span /></span></div>;

  const { country, state, city, venues, events, artists } = data;
  const trailStops = venues.filter((v) => v.lat && v.lng);
  const suggestUrl = `/community?kind=venue&location=${encodeURIComponent(`${city.name}, ${country.name}`)}&city_slug=${citySlug}&country_slug=${slug}&city_name=${encodeURIComponent(city.name)}`;

  return (
    <div data-testid={`city-page-${citySlug}`}>
      <div className="relative h-56 md:h-64">
        <img src={country.image} alt={city.name} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950/85 via-slate-950/30 to-transparent" />
        <div className="absolute bottom-0 inset-x-0 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-6">
          <Link to={`/places/${slug}`} data-testid="city-back-link" className="inline-flex items-center gap-1 text-xs font-bold text-pink-300 hover:text-pink-200 mb-2">
            <ChevronLeft className="w-3.5 h-3.5" /> {country.flag} {country.name} · {state.name}
          </Link>
          <h1 className="font-display text-4xl sm:text-5xl font-black text-white flex items-center gap-3 flex-wrap">
            {city.name} <PronounceButton text={city.name} />
          </h1>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <p className="text-slate-600 max-w-3xl leading-relaxed" data-testid="city-blurb">{blurbT[0] || city.blurb}</p>

        <AIContentBlock pageKey={`city-${slug}-${citySlug}`} topic={`The local music scene of ${city.name}, ${country.name} — venues, sessions and neighborhood sounds`} imgQuery={`${city.name} ${country.name} live music street`} />

        <section className="mt-4" data-testid="city-venues-section">
          <div className="flex items-center gap-2 mb-5 flex-wrap">
            <h2 className="font-display text-2xl font-black text-slate-900 flex items-center gap-2">
              <span className="w-9 h-9 rounded-full bg-amber-100 text-amber-600 flex items-center justify-center"><Landmark style={{ width: 18, height: 18 }} /></span>
              Local Venues &amp; Sessions
            </h2>
            <Link to={suggestUrl} data-testid="suggest-venue-btn" className="ml-auto inline-flex items-center gap-1.5 px-4 py-2 rounded-full border-2 border-pink-300 text-pink-600 text-xs font-black hover:bg-pink-50 transition-colors">
              <Plus className="w-3.5 h-3.5" /> Suggest a venue
            </Link>
          </div>
          <AIContentBlock pageKey={`city-${slug}-${citySlug}-venues`} topic={`The live venues and session culture of ${city.name}, ${country.name}`} compact />
          {venues.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {venues.map((v) => (
                <Link key={v.id} to={`/venues/${v.id}`} className="card-lift bg-white rounded-2xl border border-slate-100 overflow-hidden group block" data-testid={`venue-card-${v.id}`}>
                  <div className="h-32 overflow-hidden"><img src={v.image} alt={v.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" /></div>
                  <div className="p-4">
                    <p className="text-[10px] font-black uppercase tracking-widest text-amber-600">{v.type}</p>
                    <h3 className="font-bold text-slate-900 mt-1 group-hover:text-pink-600 transition-colors">{v.name} ›</h3>
                    <p className="text-xs text-slate-500 mt-1.5 leading-relaxed">{v.blurb}</p>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <p className="text-sm text-slate-500 bg-white rounded-2xl border border-slate-100 p-5" data-testid="city-venues-empty">
              No venues documented here yet — know a great local spot? <Link to={suggestUrl} className="text-pink-600 font-bold">Submit it for review</Link>.
            </p>
          )}
        </section>

        {trailStops.length > 0 && (
          <section className="mt-10" data-testid="city-trail-section">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <h2 className="font-display text-2xl font-black text-slate-900 flex items-center gap-2">
                <span className="w-9 h-9 rounded-full bg-purple-100 text-purple-600 flex items-center justify-center"><Footprints style={{ width: 18, height: 18 }} /></span>
                {city.name} Music Trail
              </h2>
              <Link to={`/places/${slug}/cities/${citySlug}/trail`} data-testid="trail-share-link" className="ml-auto inline-flex items-center gap-1.5 px-4 py-2 rounded-full border-2 border-purple-300 text-purple-700 text-xs font-black hover:bg-purple-50 transition-colors">
                <Share2 className="w-3.5 h-3.5" /> Share &amp; print trail
              </Link>
            </div>
            <p className="text-sm text-slate-500 mb-5 ml-11">A suggested walkable route through the city's music landmarks — follow the numbered stops.</p>
            <div className="grid lg:grid-cols-3 gap-6 items-start">
              <div className="lg:col-span-2"><TrailMap stops={trailStops} /></div>
              <ol className="space-y-3" data-testid="trail-steps">
                {trailStops.map((v, i) => (
                  <li key={v.id} className="bg-white rounded-2xl border border-slate-100 p-4 flex gap-3">
                    <span className="w-7 h-7 rounded-full bg-gradient-to-br from-rose-500 to-purple-600 text-white text-xs font-black flex items-center justify-center shrink-0">{i + 1}</span>
                    <div>
                      <Link to={`/venues/${v.id}`} className="font-bold text-sm text-slate-900 hover:text-pink-600 transition-colors" data-testid={`trail-stop-city-link-${v.id}`}>{v.name} ›</Link>
                      <p className="text-[11px] font-black uppercase tracking-wide text-amber-600">{v.type}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          </section>
        )}

        {events.length > 0 && (
          <section className="mt-10" data-testid="city-events-section">
            <h2 className="font-display text-2xl font-black text-slate-900 flex items-center gap-2 mb-5">
              <span className="w-9 h-9 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center"><CalendarDays style={{ width: 18, height: 18 }} /></span>
              Events in {city.name}
            </h2>
            <AIContentBlock pageKey={`city-${slug}-${citySlug}-events`} topic={`The live event calendar and concert culture of ${city.name}, ${country.name}`} compact />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {events.map((ev) => <EventCard key={ev.id} ev={ev} />)}
            </div>
          </section>
        )}

        {artists.length > 0 && (
          <section className="mt-10" data-testid="city-artists-section">
            <h2 className="font-display text-2xl font-black text-slate-900 flex items-center gap-2 mb-5">
              <span className="w-9 h-9 rounded-full bg-pink-100 text-pink-600 flex items-center justify-center"><Users style={{ width: 18, height: 18 }} /></span>
              Artists from {country.name}
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
              {artists.slice(0, 4).map((a) => (
                <Link key={a.slug} to={`/artists/${a.slug}`} className="card-lift bg-white rounded-2xl border border-slate-100 overflow-hidden group">
                  <div className="h-32 overflow-hidden"><img src={a.image} alt={a.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" /></div>
                  <div className="p-3">
                    <p className="font-bold text-sm text-slate-900">{a.name}</p>
                    <p className="text-xs text-slate-500 flex items-center gap-1 mt-0.5"><MapPin className="w-3 h-3 text-pink-500" /> {a.place}</p>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
